// Richiesta di creazione su misura diretta a un artigiano.
// Usata nella vetrina (modalità marketplace) e nel negozio personale.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Upload, Loader2, X, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabaseUpload } from '../lib/api';
import { supabase } from '../lib/supabase';
import { getAttributionTokenFor } from '../lib/shopSession';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Budget options - same as RequestsPage
const BUDGET_OPTIONS = [
  { value: '0-50', label: '€0 - €50', description: 'Piccoli oggetti' },
  { value: '50-100', label: '€50 - €100', description: 'Accessori semplici' },
  { value: '100-250', label: '€100 - €250', description: 'Creazioni base' },
  { value: '250-500', label: '€250 - €500', description: 'Pezzi elaborati' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Lavori complessi' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Pezzi unici' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Alto artigianato' },
  { value: '5000+', label: '€5.000+', description: 'Lusso e collezioni' },
];

// Timeline options - same as RequestsPage
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: '3months', label: '3 Mesi', description: 'Senza fretta' },
  { value: '6months', label: '6 Mesi', description: 'Progetto lungo' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];


export default function DirectRequestDialog({ crafter, open, onOpenChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [directRequestForm, setDirectRequestForm] = useState({
    title: '',
    description: '',
    budget: '',
    timeline: '',
    gift_event: '',
    gift_recipient: ''
  });
  const [directRequestAttachments, setDirectRequestAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [isGiftRequest, setIsGiftRequest] = useState(false);

  // Handle file upload for direct request
  const handleDirectRequestFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (directRequestAttachments.length + files.length > 5) {
      toast.error('Massimo 5 immagini consentite');
      return;
    }

    setUploadingAttachment(true);
    const newAttachments = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} supera i 5MB`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} non è un'immagine valida`);
        continue;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `direct-requests/${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      try {
        const { publicUrl } = await supabaseUpload('rarifex-uploads', fileName, file);
        newAttachments.push({
          name: file.name,
          url: publicUrl,
          type: file.type,
        });
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Errore upload ${file.name}`);
      }
    }

    setDirectRequestAttachments([...directRequestAttachments, ...newAttachments]);
    setUploadingAttachment(false);
    
    if (newAttachments.length > 0) {
      toast.success(`${newAttachments.length} immagine/i caricata/e`);
    }
  };

  const removeDirectRequestAttachment = (index) => {
    setDirectRequestAttachments(directRequestAttachments.filter((_, i) => i !== index));
  };

  // Handle direct request submission
  const handleSubmitDirectRequest = async () => {
    if (!user) {
      toast.error('Devi accedere per inviare una richiesta');
      navigate('/login');
      return;
    }

    if (!directRequestForm.description.trim()) {
      toast.error('Descrivi la tua idea');
      return;
    }

    setSubmittingRequest(true);
    try {
      const requestData = {
        client_id: user.id,
        crafter_id: crafter.id,
        title: directRequestForm.title || `Richiesta per ${crafter.full_name || crafter.shop_name}`,
        description: directRequestForm.description,
        category: crafter.categories?.[0] || 'Personalizzato',
        price_range: directRequestForm.budget || 'flexible',
        timeline: directRequestForm.timeline || 'flexible',
        attachments: directRequestAttachments.map(a => a.url),
        request_type: 'direct_crafter'
      };

      // Add gift fields if enabled
      if (isGiftRequest) {
        if (directRequestForm.gift_event) requestData.gift_event = directRequestForm.gift_event;
        if (directRequestForm.gift_recipient) requestData.gift_recipient = directRequestForm.gift_recipient;
      }

      // Cliente arrivato dal link della bottega: la richiesta resta attribuita all'artigiano
      const attribution = getAttributionTokenFor(crafter.id);
      if (attribution) requestData.shop_attribution = attribution;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const response = await fetch(`${API_URL}/api/requests/create-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }

      toast.success(`Richiesta inviata a ${crafter.full_name || crafter.shop_name}!`);
      onOpenChange(false);
      setDirectRequestForm({ title: '', description: '', budget: '', timeline: '', gift_event: '', gift_recipient: '' });
      setDirectRequestAttachments([]);
      setIsGiftRequest(false);
    } catch (error) {
      console.error('Error submitting direct request:', error);
      toast.error(error?.message && error.message !== 'Errore durante la creazione' ? error.message : 'Errore nell\'invio della richiesta');
    } finally {
      setSubmittingRequest(false);
    }
  };

  if (!crafter) return null;

  return (
<Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-shop-accent" />
            Richiedi creazione su misura
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Crafter Info */}
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <img 
              src={crafter?.avatar_url || '/placeholder-avatar.png'} 
              alt={crafter?.full_name}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <p className="font-medium">{crafter?.full_name || crafter?.shop_name}</p>
              <p className="text-sm text-slate-500">Riceverà la tua richiesta direttamente</p>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label className="text-sm font-medium">Titolo (opzionale)</Label>
            <Input
              value={directRequestForm.title}
              onChange={(e) => setDirectRequestForm({...directRequestForm, title: e.target.value})}
              placeholder="Es: Collana personalizzata per anniversario"
              className="mt-1"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium">Descrivi la tua idea *</Label>
            <Textarea
              value={directRequestForm.description}
              onChange={(e) => setDirectRequestForm({...directRequestForm, description: e.target.value})}
              placeholder="Descrivi cosa vorresti realizzare, materiali preferiti, dimensioni, colori..."
              className="mt-1 min-h-[120px]"
              data-testid="direct-request-description"
            />
          </div>

          {/* Image Upload */}
          <div>
            <Label className="text-sm font-medium">Immagini di riferimento (opzionale)</Label>
            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleDirectRequestFileUpload}
                className="hidden"
                id="direct-request-upload"
                disabled={uploadingAttachment}
              />
              <label
                htmlFor="direct-request-upload"
                className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  uploadingAttachment ? 'bg-slate-100 border-slate-300' : 'border-slate-300 hover:border-shop-primary hover:bg-slate-50'
                }`}
              >
                {uploadingAttachment ? (
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-slate-400 mb-1" />
                    <span className="text-xs text-slate-500">Carica immagini (max 5, 5MB ciascuna)</span>
                  </>
                )}
              </label>
            </div>
            
            {/* Thumbnails */}
            {directRequestAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {directRequestAttachments.map((att, idx) => (
                  <div key={idx} className="relative group">
                    <img 
                      src={att.url} 
                      alt={att.name}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => removeDirectRequestAttachment(idx)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Budget & Timeline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Budget</Label>
              <Select 
                value={directRequestForm.budget} 
                onValueChange={(v) => setDirectRequestForm({...directRequestForm, budget: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {BUDGET_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Tempi</Label>
              <Select 
                value={directRequestForm.timeline} 
                onValueChange={(v) => setDirectRequestForm({...directRequestForm, timeline: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Gift Toggle */}
          <div className="border border-rose-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setIsGiftRequest(!isGiftRequest)}
              className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                isGiftRequest ? 'bg-rose-50' : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Gift className={`w-4 h-4 ${isGiftRequest ? 'text-rose-500' : 'text-slate-400'}`} />
                <span className={`text-sm font-medium ${isGiftRequest ? 'text-rose-700' : 'text-slate-700'}`}>
                  È un regalo?
                </span>
              </div>
              <div className={`w-10 h-5 rounded-full transition-colors relative ${isGiftRequest ? 'bg-rose-500' : 'bg-slate-200'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${isGiftRequest ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </button>
            
            {isGiftRequest && (
              <div className="p-3 border-t border-rose-200 bg-rose-50/50 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Evento</Label>
                    <select
                      value={directRequestForm.gift_event}
                      onChange={(e) => setDirectRequestForm({...directRequestForm, gift_event: e.target.value})}
                      className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md"
                    >
                      <option value="">Seleziona...</option>
                      <option value="compleanno">Compleanno</option>
                      <option value="matrimonio">Matrimonio</option>
                      <option value="anniversario">Anniversario</option>
                      <option value="battesimo">Battesimo</option>
                      <option value="laurea">Laurea</option>
                      <option value="natale">Natale</option>
                      <option value="san_valentino">San Valentino</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Per chi?</Label>
                    <select
                      value={directRequestForm.gift_recipient}
                      onChange={(e) => setDirectRequestForm({...directRequestForm, gift_recipient: e.target.value})}
                      className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md"
                    >
                      <option value="">Seleziona...</option>
                      <option value="donna">Per una donna</option>
                      <option value="uomo">Per un uomo</option>
                      <option value="coppia">Per una coppia</option>
                      <option value="bambino">Per un bambino/a</option>
                      <option value="neonato">Per un neonato</option>
                      <option value="altro">Altro</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmitDirectRequest}
            disabled={submittingRequest || !directRequestForm.description.trim()}
            className="w-full bg-shop-accent text-white hover:bg-shop-accent/90"
            data-testid="submit-direct-request-btn"
          >
            {submittingRequest ? 'Invio in corso...' : 'Invia richiesta'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
